import { useState } from 'preact/hooks';
import cn from 'classnames';
import { ButtonQuantity } from '@nespresso-design-system/components';
import { useCart, useCartInProduct } from '@/widgets/hooks/useCart';
import type { Product } from '@/domain/Product';
import Popover from '../../containers/Popover/Popover.tsx';
import { injectRefToReactComponent } from '@/utils/injectRefToReactComponent.ts';
import QuantitySelector from './QuantitySelector.tsx';
import useMediaQuery from '@/widgets/hooks/useMediaQuery.tsx';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import type { Translations } from '@/widgets/ProductList/config/index.ts';
import style from './AddToCartButton.module.scss';

type AddToCartButtonProps = {
  product: Product;
  mode: 'icon-only' | 'icon-and-text';
};

export default function AddToCartButton({
  product,
  mode
}: AddToCartButtonProps) {
  const { updateItem } = useCart();
  const { t } = useTranslations<Translations>();
  const { isMobile } = useMediaQuery();
  const [qty, setQty] = useState<number>(0);
  const isAvailable = useCartInProduct(product, setQty);
  const step = product.salesMultiple;
  const displayBtnLabel = mode === 'icon-and-text';
  const labelPartial = getLabelPartial(qty, product.inStock);
  const label = t(`common.product.a2c.large${labelPartial}ButtonText`);

  const ariaLabel = t(
    `common.product.a2c.${
      labelPartial[0].toLowerCase() + labelPartial.slice(1)
    }ButtonAriaLabel`,
    {
      productName: product.name,
      quantity: qty
    }
  );

  return (
    <Popover
      isAnchored={!isMobile}
      renderTargetElement={({ togglePopover, ref, isOpen }) => (
        <ButtonQuantity
          title={t(
            `common.product.a2c.title${isAvailable ? '' : '.NotAvailable'}`
          )}
          aria-haspopup
          aria-expanded={isOpen}
          className={cn({
            [style.buttonWithText]: displayBtnLabel,
            [style.update]: qty > 0
          })}
          ref={injectRefToReactComponent<HTMLElement>(ref)}
          quantity={qty}
          aria-label={ariaLabel}
          data-qa={product.nessoft.legacyId}
          label={displayBtnLabel ? label : undefined}
          // @ts-ignore - DS React vs Preact types
          size="medium"
          variant="add"
          disabled={!isAvailable}
          onClick={(e) => {
            e.stopPropagation();
            togglePopover();
            // TODO: Uncomment to enable one-click to add the minimum quantity
            // TODO: Remove .skip in src/widgets/components/controls/AddToCartButton/AddToCartButton.test.tsx
            // if (qty) return;
            // setQty(step);
            // updateItem(product.id, step);
          }}
        />
      )}
      renderContent={({ togglePopover }) => (
        <QuantitySelector
          quantity={qty}
          onClose={togglePopover}
          onOptimisticUpdate={(amount) => {
            setQty(amount);
            updateItem(product.id, amount);
          }}
          step={step}
          maxOrderQuantity={product.maxOrderQuantity}
          quantities={product.quantities}
        />
      )}
    />
  );
}

function getLabelPartial(qty: number, inStock: boolean) {
  if (!inStock) return 'OutOfStock';
  return qty ? 'Update' : 'Add';
}
